import React from 'react';
import { motion } from 'framer-motion';

const Menu = () => {
  React.useEffect(() => {
    document.title = 'Menu - Royal Hooka Lounge | Premium Hookah Flavors & Beverages';
  }, []);

  const hookahFlavors = [
    { name: 'Double Apple', price: '$25', description: 'Classic Middle Eastern flavor with rich apple taste' },
    { name: 'Mint', price: '$22', description: 'Refreshing mint flavor for a cool experience' },
    { name: 'Blue Mist', price: '$28', description: 'Exotic blueberry blend with a hint of mystery' },
    { name: 'Rose', price: '$30', description: 'Delicate rose petals for a romantic session' },
    { name: 'Grape', price: '$25', description: 'Sweet grape flavor with smooth finish' },
    { name: 'Orange', price: '$24', description: 'Citrus burst with natural orange essence' },
    { name: 'Watermelon', price: '$26', description: 'Juicy watermelon for summer vibes' },
    { name: 'Vanilla', price: '$27', description: 'Creamy vanilla with rich aroma' }
  ];

  const beverages = [
    { name: 'Turkish Tea', price: '$8', description: 'Authentic Turkish tea served in traditional glasses' },
    { name: 'Arabic Coffee', price: '$10', description: 'Rich Arabic coffee with cardamom' },
    { name: 'Fresh Juice', price: '$12', description: 'Variety of fresh fruit juices' },
    { name: 'Soft Drinks', price: '$6', description: 'Selection of international soft drinks' },
    { name: 'Mineral Water', price: '$4', description: 'Premium mineral water' },
    { name: 'Energy Drinks', price: '$8', description: 'Popular energy drink brands' }
  ];

  return (
    <div className="pt-32">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <motion.h1 
            className="text-5xl font-serif font-bold text-center text-gold-400 mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Our Menu
          </motion.h1>
          <motion.p 
            className="text-xl text-center text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Discover our premium selection of hookah flavors and beverages
          </motion.p>
        </div>
      </section>

      {/* Hookah Flavors */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.h2 
            className="text-4xl font-serif font-bold text-center text-gold-400 mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Premium Hookah Flavors
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {hookahFlavors.map((flavor, index) => (
              <motion.div 
                key={index}
                className="bg-gray-900 p-6 rounded-lg border border-gold-500/20 hover:border-gold-500/40 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-semibold text-gold-400">{flavor.name}</h3>
                  <span className="text-lg font-bold text-gold-300">{flavor.price}</span>
                </div>
                <p className="text-gray-300 text-sm">{flavor.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Beverages */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <motion.h2 
            className="text-4xl font-serif font-bold text-center text-gold-400 mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Beverages
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {beverages.map((beverage, index) => (
              <motion.div 
                key={index}
                className="bg-black/50 p-6 rounded-lg border border-gold-500/20 hover:border-gold-500/40 transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-semibold text-gold-400">{beverage.name}</h3>
                  <span className="text-lg font-bold text-gold-300">{beverage.price}</span>
                </div>
                <p className="text-gray-300 text-sm">{beverage.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Offers */}
      <section className="py-20 bg-gradient-to-r from-gold-600 to-gold-800">
        <div className="container mx-auto px-4 text-center">
          <motion.h2 
            className="text-4xl font-serif font-bold text-black mb-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Special Offers
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div 
              className="bg-black/20 p-6 rounded-lg"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-black mb-3">Happy Hour</h3>
              <p className="text-black">20% off all hookah flavors from 3-6 PM daily</p>
            </motion.div>
            <motion.div 
              className="bg-black/20 p-6 rounded-lg"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-black mb-3">Group Package</h3>
              <p className="text-black">Buy 3 hookahs and get 1 free for groups of 6+</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Menu;